<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import {
  FileOutput,
  Copy,
  Trash2,
  Save,
  Loader2,
  Eye,
  Pencil,
  Download,
} from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { MdPreview, MdEditor } from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import { useCopilotStore } from '../store'
import { toast } from 'vue-sonner'
import { useMarkdownPdf } from '@/composables/useMarkdownPdf'
import type { DocumentType, DocumentEntityType } from '@/types'

const copilotStore = useCopilotStore()
const { projectContext } = storeToRefs(copilotStore)

// Local editable state
const editTitle = ref('')
const editContent = ref('')
const editDocType = ref<DocumentType>('other')
const editTags = ref<string[]>([])
const isEditing = ref(true)
const showSaveDialog = ref(false)
const isSaving = ref(false)
const saveEntityType = ref<DocumentEntityType>('project')

// PDF export
const pdfFilename = computed(() => editTitle.value || 'documento')
const { isGenerating: isGeneratingPdf, downloadPdf } = useMarkdownPdf(editContent, pdfFilename)

const hasDocument = computed(() => !!editContent.value)

const documentTypeOptions: Array<{ value: DocumentType; label: string }> = [
  { value: 'scope', label: 'Alcance' },
  { value: 'architecture', label: 'Arquitectura' },
  { value: 'specifications', label: 'Especificaciones' },
  { value: 'manual', label: 'Manual' },
  { value: 'other', label: 'Otro' },
]

const handleCopy = async () => {
  try {
    await navigator.clipboard.writeText(editContent.value)
    toast.success('Contenido copiado al portapapeles')
  } catch {
    toast.error('Error al copiar')
  }
}

const handleDiscard = () => {
  editTitle.value = ''
  editContent.value = ''
  editDocType.value = 'other'
  editTags.value = []
  isEditing.value = true
}

const handleSave = async () => {
  if (!projectContext.value || !editTitle.value || !editContent.value) return

  isSaving.value = true
  try {
    const doc = {
      title: editTitle.value,
      content: editContent.value,
      documentType: editDocType.value,
      suggestedTags: editTags.value,
    }

    const entityId = projectContext.value.projectId

    await copilotStore.saveGeneratedDocument(doc, saveEntityType.value, entityId)
    toast.success('Documento guardado exitosamente')
    showSaveDialog.value = false
    handleDiscard()
  } catch (error) {
    console.error('Error saving document:', error)
    toast.error('Error al guardar el documento')
  } finally {
    isSaving.value = false
  }
}

const removeTag = (index: number) => {
  editTags.value.splice(index, 1)
}
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Header -->
    <div class="border-b px-4 py-2.5 flex items-center justify-between shrink-0">
      <div class="flex items-center gap-2">
        <FileOutput class="h-4 w-4" />
        <span class="text-sm font-medium">Output</span>
      </div>
      <div v-if="hasDocument" class="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          class="h-7 w-7"
          @click="isEditing = !isEditing"
        >
          <Eye v-if="isEditing" class="h-3.5 w-3.5" />
          <Pencil v-else class="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="!hasDocument"
      class="flex-1 flex items-center justify-center p-8"
    >
      <div class="text-center space-y-3">
        <FileOutput class="h-12 w-12 mx-auto text-muted-foreground/40" />
        <div>
          <p class="text-sm font-medium">Panel de Output</p>
          <p class="text-xs text-muted-foreground mt-1">
            Pega contenido del chat aqui para editarlo y guardarlo como documento
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          class="text-xs"
          @click="isEditing = true; editContent = ' '"
        >
          <Pencil class="h-3 w-3 mr-1" />
          Comenzar a escribir
        </Button>
      </div>
    </div>

    <!-- Document Preview/Editor -->
    <template v-else>
      <!-- Document Meta -->
      <div class="p-3 border-b space-y-2 shrink-0">
        <Input
          v-model="editTitle"
          placeholder="Titulo del documento"
          class="text-sm font-medium h-8"
        />
        <div class="flex gap-2">
          <Select v-model="editDocType">
            <SelectTrigger class="h-7 text-xs flex-1">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent class="z-[200]">
              <SelectItem
                v-for="opt in documentTypeOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div v-if="editTags.length > 0" class="flex flex-wrap gap-1">
          <Badge
            v-for="(tag, idx) in editTags"
            :key="idx"
            variant="secondary"
            class="text-xs cursor-pointer"
            @click="removeTag(idx)"
          >
            {{ tag }} &times;
          </Badge>
        </div>
      </div>

      <!-- Content -->
      <ScrollArea class="flex-1">
        <div v-if="isEditing" class="p-0">
          <MdEditor
            v-model="editContent"
            language="en-US"
            :preview="false"
            :toolbars="['bold', 'italic', 'strikeThrough', '-', 'title', 'sub', 'sup', '-', 'unorderedList', 'orderedList', 'task', '-', 'codeRow', 'code', 'link', 'table', '-', 'revoke', 'next']"
            class="copilot-editor"
            style="height: 100%"
          />
        </div>
        <div v-else class="p-4">
          <MdPreview
            :model-value="editContent"
            language="en-US"
            class="copilot-output-preview"
          />
        </div>
      </ScrollArea>

      <!-- Actions -->
      <div class="border-t p-3 space-y-2 shrink-0">
        <div class="flex gap-2">
          <Button
            class="flex-1 text-xs h-8"
            @click="showSaveDialog = true"
            :disabled="!editTitle || !editContent"
          >
            <Save class="h-3 w-3 mr-1" />
            Guardar como Documento
          </Button>
        </div>
        <div class="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            class="flex-1 text-xs h-7"
            @click="downloadPdf"
            :disabled="isEditing || isGeneratingPdf"
          >
            <Loader2 v-if="isGeneratingPdf" class="h-3 w-3 mr-1 animate-spin" />
            <Download v-else class="h-3 w-3 mr-1" />
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            class="flex-1 text-xs h-7"
            @click="handleCopy"
          >
            <Copy class="h-3 w-3 mr-1" />
            Copiar
          </Button>
          <Button
            variant="outline"
            size="sm"
            class="flex-1 text-xs h-7"
            @click="handleDiscard"
          >
            <Trash2 class="h-3 w-3 mr-1" />
            Descartar
          </Button>
        </div>
      </div>
    </template>

    <!-- Save Dialog -->
    <Dialog v-model:open="showSaveDialog">
      <DialogContent class="z-[200]">
        <DialogHeader>
          <DialogTitle>Guardar Documento</DialogTitle>
          <DialogDescription>
            El documento se guardara en el proyecto seleccionado.
          </DialogDescription>
        </DialogHeader>

        <div class="space-y-4 py-4">
          <div class="space-y-2">
            <label class="text-sm font-medium">Titulo</label>
            <Input v-model="editTitle" class="text-sm" />
          </div>

          <div class="space-y-2">
            <label class="text-sm font-medium">Tipo de Documento</label>
            <Select v-model="editDocType">
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar tipo" />
              </SelectTrigger>
              <SelectContent class="z-[200]">
                <SelectItem
                  v-for="opt in documentTypeOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div class="space-y-2">
            <label class="text-sm font-medium">Guardar en</label>
            <Select v-model="saveEntityType">
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar destino" />
              </SelectTrigger>
              <SelectContent class="z-[200]">
                <SelectItem value="project">Proyecto</SelectItem>
                <SelectItem value="application">Aplicacion</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div v-if="projectContext" class="bg-muted rounded-md p-3">
            <p class="text-xs text-muted-foreground">
              Se guardara en: <strong>{{ projectContext.projectName }}</strong>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" @click="showSaveDialog = false">
            Cancelar
          </Button>
          <Button @click="handleSave" :disabled="isSaving || !editTitle">
            <Loader2 v-if="isSaving" class="h-4 w-4 mr-2 animate-spin" />
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>

<style>
.copilot-output-preview .md-editor-preview-wrapper {
  padding: 0 !important;
}
.copilot-editor .md-editor {
  border: none !important;
}
</style>
